import { useMemo } from "react";

import { faEquals } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { Card } from "src/base-components/Card";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { Divider } from "src/base-components/Divider";
import { Icon } from "src/base-components/Icon";
import { RequiredAsterisk } from "src/base-components/RequiredAsterisk";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { Switch } from "src/base-components/Switch";
import { getSchemaIcon } from "src/entities/entityView/utils";
import { EntitySchemaResource } from "src/entities/queries";
import { ReadEntityNodeForm } from "src/entityActionNodes/ReadEntityNodeEditor";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { SchemaField } from "src/parentFlowNodes/SchemaField";

export const CustomTitle: React.FC<{
  title: string;
  description?: string;
  required?: boolean;
}> = ({ title, description, required = false }) => {
  return (
    <h3 className="pb-3 font-inter-semibold-13px text-gray-800">
      <span className="pr-1 pb-1">{title}</span>
      {required && <RequiredAsterisk />}
      {description && (
        <p className="font-inter-normal-12px text-gray-500">{description}</p>
      )}
    </h3>
  );
};

export const LocateEntityCard = ({
  immutable,
  entitySchemas,
  isLoading,
  fieldErrors,
}: {
  immutable: boolean;
  entitySchemas: EntitySchemaResource[];
  isLoading: boolean;
  fieldErrors: FieldErrors;
}) => {
  const { control, getValues, watch } = useFormContext<ReadEntityNodeForm>();
  const entitySchema = watch("entity_schema");

  const selectedSchema = useMemo(() => {
    return entitySchemas.find((schema) => schema._id === entitySchema);
  }, [entitySchemas, entitySchema]);

  const entityIdFieldId = getValues("entity_ids_expression.id");
  const entityIdError = fieldErrors[entityIdFieldId];
  const isSearchHighlighted = useIsFieldHighlighted(entityIdFieldId);

  return (
    <Card>
      <Card.Header className="flex items-center gap-x-3">
        <CustomTitle
          description="Select the field that contains the Entity's ID(s). It will be used to fetch the Entity or the list of Entities."
          title="Locate the Entity"
          required
        />
      </Card.Header>
      <Card.Content className="space-y-4">
        <div className="flex gap-x-3 font-inter-normal-12px text-gray-500">
          <div className="flex flex-1 flex-col gap-y-2">
            <p>Current Decision Flow</p>
            <div>
              <Controller
                control={control}
                name="entity_ids_expression.value"
                render={({ field }) => (
                  <AutocompleteCodeInput
                    dataLoc="entity-ids-expression"
                    disabled={immutable}
                    error={entityIdError}
                    isSearchHighlighted={isSearchHighlighted}
                    placeholder="data.entity_id(s)"
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>
          </div>
          <div className="flex items-end pb-1.5 align-bottom">
            <Icon color="text-gray-500" icon={faEquals} size="xs" />
          </div>
          <div className="flex flex-1 flex-col gap-y-2">
            <div className="flex gap-x-2">
              {isLoading && <SkeletonPlaceholder height="h-5" width="w-20" />}
              {!isLoading && selectedSchema && (
                <>
                  <Icon
                    color="text-gray-500"
                    icon={getSchemaIcon(selectedSchema)}
                    size="xs"
                  />
                  <span>{selectedSchema?._display_name_singular}</span>
                </>
              )}
              {!isLoading && !selectedSchema && "Entity"}
            </div>
            <SchemaField
              dataLoc="entity-id"
              field={{
                fieldName: "entity_id",
                required: true,
                sensitive: false,
                type: ["string", false],
              }}
              name="Entity ID"
              nullable={false}
              required={true}
              sensitive={false}
              tooltipBody=""
              type="string"
            />
          </div>
        </div>
        <Divider variant="light" />
        <div className="flex gap-x-2">
          <CustomTitle
            description="When enabled, Entity is enriched with all its to-one relationships. This allows you to reference those fields later in your Decision Flow."
            title="Enrich with related data"
          />
          <Controller
            control={control}
            name="enrich_related_data"
            render={({ field }) => (
              <Switch
                dataLoc="enrich-related-data"
                disabled={immutable}
                enabled={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      </Card.Content>
    </Card>
  );
};
